/**
 * 应用 Providers 组合 - 统一管理所有 Context
 * 
 * 功能特性：
 * - 合并 ThemeProvider 和 I18nProvider
 * - 避免 ConfigProvider 重复嵌套
 * - 统一配置 Ant Design
 */

import type { ReactNode } from 'react';
import { useMemo, useEffect, useState } from 'react';
import { ConfigProvider, App } from 'antd';
import { useTranslation } from 'react-i18next';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import { 
  colorTokens,
  generateCSSVariables,
  getTheme
} from '../styles/tokens';
import { getAntdTheme } from '../styles/theme/antdTheme';
import { ThemeContext } from './ThemeContext';
import { I18nContext, LANGUAGE_CONFIG } from './I18nContext';
import type { SupportedLanguage } from './I18nContext';

interface AppProvidersProps {
  children: ReactNode;
}

// 语言到 Ant Design locale 的映射
const ANTD_LOCALE_MAP = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en': enUS,
} as const;

/**
 * 获取存储的语言
 */
const getStoredLanguage = (): SupportedLanguage | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem('changmei-language');
    if (stored && Object.keys(LANGUAGE_CONFIG).includes(stored)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // 忽略存储错误
  }
  
  return null;
};

/**
 * 获取浏览器语言
 */
const getBrowserLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'zh-CN';
  
  const browserLang = navigator.language || navigator.languages?.[0];
  
  if (browserLang?.startsWith('zh')) {
    if (browserLang.includes('TW') || browserLang.includes('HK') || browserLang.includes('MO')) {
      return 'zh-TW';
    }
    return 'zh-CN';
  }
  
  if (browserLang?.startsWith('en')) {
    return 'en';
  }
  
  return 'zh-CN';
};

/**
 * 存储语言
 */
const storeLanguage = (language: SupportedLanguage): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem('changmei-language', language);
  } catch {
    // 忽略存储错误
  }
};

/**
 * 应用 Providers 组合组件
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const { i18n, t } = useTranslation();
  
  // 初始化语言状态
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    return getStoredLanguage() || getBrowserLanguage();
  });

  // 初始化主题
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    
    // 添加主题类名
    root.classList.add('theme-light');
    root.setAttribute('data-theme', 'light');
    
    // 应用 Design Token 生成的 CSS 变量
    const designTokens = getTheme('light');
    const cssVariables = generateCSSVariables(designTokens);
    
    // 应用 CSS 变量到根元素
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, []);

  // 同步 i18next 语言
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // 切换语言
  const changeLanguage = (newLanguage: SupportedLanguage) => {
    setLanguageState(newLanguage);
    storeLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  // 获取语言配置
  const getLanguageConfig = (lang: SupportedLanguage) => {
    return LANGUAGE_CONFIG[lang];
  };

  // 支持的语言列表
  const supportedLanguages: SupportedLanguage[] = Object.keys(LANGUAGE_CONFIG) as SupportedLanguage[];

  // Theme Context 值
  const themeContextValue = useMemo(() => ({
    colorTokens,
  }), []);

  // I18n Context 值
  const i18nContextValue = useMemo(() => ({
    language,
    changeLanguage,
    getLanguageConfig,
    supportedLanguages,
    t,
  }), [language, changeLanguage, supportedLanguages, t]);

  // Ant Design 主题配置
  const antdThemeConfig = useMemo(() => getAntdTheme('light'), []);

  // Ant Design 国际化配置
  const antdLocale = useMemo(() => ANTD_LOCALE_MAP[language], [language]);

  return (
    <I18nContext.Provider value={i18nContextValue}>
      <ThemeContext.Provider value={themeContextValue}>
        <ConfigProvider 
          theme={antdThemeConfig}
          locale={antdLocale}
        >
          <App>
            {children}
          </App>
        </ConfigProvider>
      </ThemeContext.Provider>
    </I18nContext.Provider>
  );
};

export default AppProviders;
